import React from 'react';
import "./smile-pages.css";

export default function HowDoesSmilingBoostEnergy() {
  return (
    <div className="container-box mt-5 mb-5 smile-page">

      <h1>How does Smiling Boost Energy?</h1>

      <p>
        Smiling boosts energy by triggering <strong>physiological and psychological changes </strong> 
        in your body and brain that make you feel more <strong>awake, alert, and motivated</strong>.
        Even subtle or intentional smiling creates an upward shift in energy levels.
      </p>

      <p>Here’s how it works, backed by science:</p>

      <hr />

      {/* 1 */}
      <h3>⚡ 1. Smiling Releases Energizing Neurochemicals</h3>

      <p>
        When you smile, your brain releases a cocktail of <strong>“feel-good” chemicals</strong>:
      </p>

      <ul>
        <li><strong>Dopamine</strong> – boosts motivation and energy</li>
        <li><strong>Endorphins</strong> – reduce fatigue and increase pleasure</li>
        <li><strong>Serotonin</strong> – stabilizes mood and regulates sleep/wake cycles</li>
      </ul>

      <p>
        These neurochemicals can give you a <strong>natural energy lift</strong> —
        like a mini mood-enhancing shot for your nervous system.
      </p>

      <p><strong>📚 Source:</strong></p>
      <ul>
        <li>
          Niedenthal, P. (2007). <em>Embodied emotion: the role of facial expression in emotional experience.</em>
        </li>
      </ul>

      <hr />

      {/* 2 */}
      <h3>💕 2. Smiling Reduces Stress, Conserving Energy</h3>

      <p>
        Stress is exhausting — it raises <strong>cortisol</strong>, increases muscle tension,
        and drains your physical and mental resources. Smiling can:
      </p>

      <ul>
        <li><strong>Lower cortisol</strong></li>
        <li>Relax your body</li>
        <li>Help you recover faster from stress</li>
      </ul>

      <p>
        By reducing stress, smiling frees up <strong>mental and physical energy </strong>
        that you can redirect toward focus, creativity, and productivity.
      </p>

      <p><strong>📚 Study:</strong></p>
      <ul>
        <li>
          Kraft & Pressman (2012) – Smiling during stressful tasks lowered heart rate and improved recovery.
        </li>
      </ul>

      <hr />

      {/* 3 */}
      <h3>🧠 3. Smiling Triggers the Facial Feedback Loop</h3>

      <p>
        The <strong>Facial Feedback Hypothesis</strong> suggests that facial expressions like smiling
        don’t just reflect emotion — <strong>they influence it</strong>. When you smile:
      </p>

      <ul>
        <li>Your brain receives a signal that things are okay</li>
        <li>This reduces mental fatigue</li>
        <li>Improves your <strong>perceived energy levels</strong></li>
        <li>Increases psychological resilience</li>
      </ul>

      <p><strong>📚 Study:</strong></p>
      <ul>
        <li>
          Strack, Martin & Stepper (1988) – People who were made to smile felt more positive and engaged.
        </li>
      </ul>

      <hr />

      {/* 4 */}
      <h3>🧘‍♂️ 4. Smiling Improves Breathing and Oxygen Flow</h3>

      <p>When you smile:</p>

      <ul>
        <li>You tend to breathe more deeply and slowly</li>
        <li><strong>Oxygen intake increases</strong>, boosting alertness and energy</li>
        <li>Improved oxygen helps your brain and muscles function better</li>
      </ul>

      <p>
        This subtle shift can be especially powerful when combined with laughter or deep breathing exercises.
      </p>

      <hr />

      {/* 5 */}
      <h3>🤝 5. Smiling Increases Social Energy</h3>

      <p>Positive social interactions sparked by smiling can significantly boost your energy:</p>

      <ul>
        <li>Positive feedback from others reduces emotional exhaustion</li>
        <li>Feeling connected increases energy and lowers burnout risk</li>
        <li>Social warmth strengthens motivation and engagement</li>
      </ul>

      <p><strong>📚 Source:</strong></p>
      <ul>
        <li>
          Fredrickson, B.L. (2001). <em>The broaden-and-build theory of positive emotions.</em>
        </li>
      </ul>

      <hr />

      {/* SUMMARY TABLE */}
      <h3>✅ Summary: How Smiling Boosts Energy</h3>

      <table className="table table-bordered mt-3">
        <thead>
          <tr>
            <th>Mechanism</th>
            <th>Energy Effect</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Dopamine and endorphin release</td>
            <td>Enhances motivation and mental energy</td>
          </tr>
          <tr>
            <td>Reduced cortisol and stress</td>
            <td>Frees up physical and emotional resources</td>
          </tr>
          <tr>
            <td>Facial feedback loop</td>
            <td>Improves perceived energy and mood</td>
          </tr>
          <tr>
            <td>Better oxygen flow</td>
            <td>Increases alertness and brain function</td>
          </tr>
          <tr>
            <td>Positive social interaction</td>
            <td>Boosts emotional energy and engagement</td>
          </tr>
        </tbody>
      </table>

      <hr />

      {/* KEYS TO REMEMBER */}
      <h3>🔑 Keys to Remember</h3>

      <p>
        <strong>Smiling gives you a natural energy boost</strong> by improving brain chemistry,
        calming stress responses, enhancing oxygen flow, and promoting energizing social interactions.
        Even a small smile can shift your internal state from drained to recharged.
      </p>
      <p>
        <strong> That Smile App is here to help give you an energy boost.</strong>
      </p>

    </div>
  );
}

